import type { ResponseDecoder } from '@krp/client-runtime'

/**
 * Конверт ответа бизнес-операции 1С (business-openapi-v1): ресурс, ревизия
 * конфигурации, по которой работала служба, корреляция и данные.
 *
 * Декодер проверяет, что ответ пришёл ПРО ТОТ ЖЕ rid, что в запросе: ответ
 * чужой базы с тем же RefKey — ошибка `invalid-response`, а не карточка не
 * того товара (D-064). Ревизия нужна командам: её возвращают как
 * `expectedConfigurationVersion`.
 */
export interface OneCEnvelope<T> {
  resourceId: string
  configurationVersion: string
  data: T
}

export function fail(message: string): never {
  throw new Error(message)
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function text(value: unknown, field: string): string {
  return typeof value === 'string' ? value : fail(`поле ${field} — не строка`)
}

export function optionalText(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null
  return text(value, field)
}

export function bool(value: unknown, field: string): boolean {
  return typeof value === 'boolean' ? value : fail(`поле ${field} — не логическое`)
}

export function num(value: unknown, field: string): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fail(`поле ${field} — не число`)
}

export function list<T>(value: unknown, field: string, item: (raw: unknown, index: number) => T): T[] {
  if (!Array.isArray(value)) fail(`поле ${field} — не массив`)
  return value.map(item)
}

/** `ResourceId` сериализуется объектом `{ value }`; строкой — тоже принимаем. */
function ridOf(value: unknown): string {
  if (typeof value === 'string') return value
  if (isRecord(value)) return text(value.value, 'resourceId.value')
  return fail('поле resourceId отсутствует')
}

export function sameResource(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase()
}

export function onecEnvelopeDecoder<T>(
  resourceId: string,
  decodeData: (raw: unknown) => T,
): ResponseDecoder<OneCEnvelope<T>> {
  return (raw) => {
    if (!isRecord(raw)) fail('ответ 1С — не объект')
    const answered = ridOf(raw.resourceId)
    if (!sameResource(answered, resourceId)) fail('ответ пришёл о другом ресурсе')
    const configurationVersion = text(raw.configurationVersion, 'configurationVersion')
    return { resourceId, configurationVersion, data: decodeData(raw.data) }
  }
}

/** Необязательное число: нет или `null` — `null`. */
export function optionalNum(value: unknown, field: string): number | null {
  if (value === undefined || value === null) return null
  return num(value, field)
}

/** Целое число: дробное и нечисловое — отказ с именем поля. */
export function int(value: unknown, field: string): number {
  const parsed = num(value, field)
  return Number.isInteger(parsed) ? parsed : fail(`поле ${field} — не целое число`)
}

/** Необязательное целое: нет или `null` — `null`. */
export function optionalInt(value: unknown, field: string): number | null {
  if (value === undefined || value === null) return null
  return int(value, field)
}

/** Перечисление сервера: значение из закрытого списка, иначе отказ с именем поля. */
export function oneOf<T extends string>(value: unknown, field: string, allowed: readonly T[]): T {
  const raw = text(value, field)
  const found = allowed.find((item) => item === raw)
  return found ?? fail(`поле ${field} — неизвестное значение «${raw}»`)
}
